import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import {
  datasetSubmissionDatasetIdToFormValues,
  datasetSubmissionStatusFilterToFormValues,
  datasetSubmissionTransactionStatusDropdownOptions,
  formValuesToDatasetSubmissionDatasetId,
  formValuesToDatasetSubmissionStatusFilter,
} from "#src/components/Workflows/WorkflowDatasetSubmissionTrigger.helpers";
import {
  DropdownInput,
  Form,
  Skeleton,
  useForm,
} from "@validereinc/common-components";
import { TransactionStatusType } from "@validereinc/domain";
import React, { useEffect } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

type WorkflowTriggerDatasetSubmissionFormValues = {
  dataset_ids: string[];
  status_filters: TransactionStatusType[];
};

const WorkflowConfigurationTriggerDatasetSubmission = () => {
  const {
    setTriggerDatasetSubmission,
    setTriggerDatasetSubmissionIsValid,
    getTriggerDatasetSubmission,
  } = useWorkflowConfigurationFormStore();

  const storeValues = getTriggerDatasetSubmission();

  const form = useForm<WorkflowTriggerDatasetSubmissionFormValues>({
    // Multi-select DropdownInput normalizes its value via an effect on mount,
    // which fires onChange before the user interacts. onTouched avoids validating
    // on that synthetic change (see ComboboxInput in DropdownInput.tsx).
    mode: "onTouched",
    values: {
      dataset_ids: datasetSubmissionDatasetIdToFormValues(
        storeValues?.dataset_id
      ),
      status_filters: datasetSubmissionStatusFilterToFormValues(
        storeValues?.status_filter
      ),
    },
  });

  const { data: datasetsData, isLoading: isLoadingDatasets } =
    useListDatasets();

  const datasetOptions =
    datasetsData?.data?.map((dataset) => ({
      id: dataset.id,
      name: dataset.name,
    })) ?? [];

  useEffect(() => {
    setTriggerDatasetSubmissionIsValid(form.formState.isValid);
  }, [form.formState.isValid, setTriggerDatasetSubmissionIsValid]);

  const syncToStore = ({
    dataset_ids,
    status_filters,
  }: WorkflowTriggerDatasetSubmissionFormValues) => {
    setTriggerDatasetSubmission({
      dataset_id: formValuesToDatasetSubmissionDatasetId(dataset_ids),
      status_filter: formValuesToDatasetSubmissionStatusFilter(status_filters),
    });
  };

  return (
    <Form {...form}>
      <Skeleton isLoading={isLoadingDatasets}>
        <DropdownInput
          name="dataset_ids"
          options={datasetOptions}
          labelKey="name"
          valueKey="id"
          placeholder="Select datasets..."
          label="Dataset"
          isRequired
          isMulti
          onChange={(dataset_ids: string[]) => {
            syncToStore({
              dataset_ids,
              status_filters: form.getValues("status_filters"),
            });
          }}
        />
        <DropdownInput
          name="status_filters"
          options={datasetSubmissionTransactionStatusDropdownOptions}
          labelKey="label"
          valueKey="value"
          placeholder="Select transaction statuses..."
          label="Transaction Status"
          description="If no statuses are configured, workflow will trigger on each status."
          isMulti
          onChange={(status_filters: TransactionStatusType[]) => {
            syncToStore({
              dataset_ids: form.getValues("dataset_ids"),
              status_filters,
            });
          }}
        />
      </Skeleton>
    </Form>
  );
};

export default WorkflowConfigurationTriggerDatasetSubmission;
